import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { Dropdown, MenuItem, getThemeManager, Switch, SwitchEvent, Overlay, dom } from '@omegagrid/core';
import { msg, getLocale, LocaleId } from '@omegagrid/localize';
import rp from '@reporting/rp-app-host';
import { AppComponent } from '../component';
import { getInitials } from '../helpers';
import { style, userMenuContentStyle } from './userMenu.style';
import { HelpMenu } from './helpMenu';
import { GlobalRights } from '@reporting/rp-types/core';
import type { SsoLink } from '@reporting/rp-auth-client-v2';

const langs: Record<LocaleId, string> = {
	en: 'English',
	cs: 'Čeština',
};

@customElement('rp-app-usermenu')
export class UserMenu extends AppComponent {

	static styles = style;

	@property({type: Boolean})
	compact = false;

	@query('.bar')
	bar: HTMLDivElement;

	@query('og-dropdown')
	dropdown: Dropdown;

	/** SA only: other instances (legacy or new) to sign in to by the hash-based SSO, see _openSsoLink */
	@state()
	ssoLinks: SsoLink[] = [];

	_loaderRef = createRef<Overlay>();
	get loader() { return this._loaderRef.value }

	_menuRef = createRef<HTMLDivElement>();
	get menu() { return this._menuRef.value }

	get user() {
		return rp.userProvider?.user;
	}

	get isImpersonating() {
		return !!rp.userProvider?.impersonator;
	}

	get hasDB2() {
		const a = rp.accountingProvider.accounting;
		return !!(a.master_db_name_2 || a.data_db_name_2 || a.mdm_db_name_2);
	}

	get formattedUserName() {
		if (this.isImpersonating) {
			return html`${rp.userProvider.impersonator_dv ?? rp.userProvider.impersonator} ${msg('as')} ${this.user.login_name ?? this.user.user_name}`;
		} else {
			return html`
				<b>${this.user.login_name ?? this.user.user_name}</b>
				${this.user.email && this.user.email !== this.user.login_name ? html`<br>${this.user.email}` : ''}
			`;
		}
	}

	async connectedCallback() {
		super.connectedCallback();
		if (rp.userProvider?.isSA()) {
			this.ssoLinks = await rp.auth.client.ssoLinks(rp.accountingProvider.a).catch((): SsoLink[] => []);
		}
	}

	async firstUpdated() {
		// fix dropdown style
		await this.dropdown.updateComplete;
		const btn = (this.dropdown.shadowRoot.querySelector('og-button') as HTMLElement);
		btn.style.border = 'none';
		btn.style.padding = '0';
	}

	userAvatar(size = '2.5em') {
		const initials = getInitials(this.user.login_name ?? this.user.user_name);
		return html`<span style="
			display: inline-flex; align-items: center; justify-content: center;
			width: ${size}; height: ${size}; border-radius: 50%;
			background: var(--og-colortype-accent-background-color);
			color: var(--og-colortype-accent-text-color);
			font-weight: bold; font-size: calc(${size} * 0.4);
			flex-shrink: 0;">${initials}</span>`;
	}

	getThemeItems(): MenuItem[] {
		const items: MenuItem[] = [];
		const themes = Array.from(rp.theme.manager.themes.values());
		const light = themes.filter(t => t.type === 'light');

		const activate = (name: string) => {
			rp.theme.activate(name);
			rp.settings.set('theme', name);
		};

		if (light.length) {
			items.push({
				key: 'light',
				value: msg('Light'),
				type: 'label',
				icon: 'sun'
			});
			light.forEach(t => items.push({
				key: t.name,
				value: t.name,
				exec: () => activate(t.name)
			}));
		}

		const dark = themes.filter(t => t.type === 'dark');
		if (dark.length) {
			items.push({
				key: 'dark',
				value: msg('Dark'),
				type: 'label',
				icon: 'moon'
			});
			dark.forEach(t => items.push({
				key: t.name,
				value: t.name,
				exec: () => activate(t.name)
			}));
		}

		return items;
	}

	getLangItems(): MenuItem[] {
		return Object.entries(langs).map(([key, value]) => ({
			key,
			value,
			exec: async () => await this.changeLocale(key as LocaleId),
		}));
	}

	async changeLocale(locale: LocaleId) {
		this.loader.show(this.menu);

		await rp.ms('users').patch('lang', { lang: locale });
		await rp.localization.setLocale(locale);

		this.loader?.hide();
	}

	private _stopImpersonate = async () => {
		await rp.auth.client.stopImpersonate();
		rp.userProvider.impersonator = null;
		rp.router.clearHostState();
		rp.create();
	}

	private _openUserAccount = () => {
		this.dropdown.close();
		if (rp.host.bucket.dialogs.items.has(`users-userForm-user-0-${this.user.user_name}`)) return;
		rp.host.bucket.openDialog({
			id: `users-userForm-user-0-${this.user.user_name}`,
			caption: msg('User account'),
			closable: true,
			// the form stack is ~680 px tall; small laptops and phones get what fits (the container scrolls)
			width: Math.min(500, rp.host.offsetWidth - 20),
			height: Math.min(680, rp.host.offsetHeight - 20),
			resizable: true,
			location: 'center',
		});
	}

	/** the instance in a new window, signed in by an SSO link the server signs now (valid for a few minutes) */
	private _openSsoLink = async (link: SsoLink) => {
		this.dropdown.close();
		// opened right in the click: a window opened after the request would be blocked as a popup
		const win = window.open('', '_blank');
		if (win) win.opener = null;
		try {
			const url = await rp.auth.client.ssoLinkUrl(link.id, rp.accountingProvider.a);
			if (win) win.location.href = url;
			else window.location.href = url;
		} catch (e) {
			win?.close();
			throw e;
		}
	};

	private _openUserSettings = () => {
		this.dropdown.close();
		rp.host.open(`users-settings`, {title: msg('Settings')});
	};

	_toggleDB2 = async (e: SwitchEvent) => {
		this.dropdown.close();
		const command = await this.host.openMenu({
			content: 'yes_no',
			text: msg('Are you sure you want to switch database? Make sure to save your work before switching, as unsaved changes might be lost.'),
		});
		if (command?.id === 'Y') {
			this.loader.show(this.menu);
			await rp.setDB(e.value === 'on' ? 2 : 1, true);
			this.loader?.hide();
		} else {
			const switchElm = e.target as Switch;
			switchElm.state = e.value === 'on' ? 'off' : 'on';
			switchElm.checkbox.checked = switchElm.state === 'on';
		}
	}

	/** compact: the header has no room for rp-app-helpmenu (see AppMenu), its items open in a dialog instead of a dropdown */
	private _openHelp = async () => {
		this.dropdown.close();
		const helpMenu = dom.createElement<HelpMenu>('rp-app-helpmenu', { inline: true });
		const dialog = await rp.host.openDialog({
			component: helpMenu,
			caption: msg('Help'),
			closable: true,
			scrollable: true,
			location: 'center',
			width: Math.min(320, rp.host.offsetWidth - 20),
			height: Math.min(480, rp.host.offsetHeight - 20),
		});
		// an item opened something (dialog, sidebar, tool): the menu is done
		helpMenu.addEventListener('helpmenu.close', () => dialog.close());
	};

	render = () => html`
		<og-dropdown
			color="${this.isImpersonating ? 'yellow' : 'transparent'}"
			@dropdown.open="${() => this.layout()}"
			nocaret>

			<div slot="label">
				${rp.db > 1 ? html`
					<span style="margin-right: 14px; display: flex;">
						<og-icon
							.icon="${{ icon: ['fas', 'database'] }}"
							color="var(--og-colortype-blue-border-color)">
						</og-icon>
						<span style="color: var(--og-colortype-blue-border-color); font-weight: bold;">DB${rp.db}</span>
					</span>
				` : null}

				${this.userAvatar()}

			</div>

			<div slot="content" class="usermenu-content" ${ref(this._menuRef)}>
				<style>${userMenuContentStyle}</style>
				<og-overlay defaultLoader ${ref(this._loaderRef)} id="user-menu-overlay"></og-overlay>

				<span class="user-header">
					${this.compact ? null : this.userAvatar()}
					<span>${this.formattedUserName}</span>
				</span>

				${this.isImpersonating ? html`
					<og-button
						@click="${this._stopImpersonate}"
						color="transparent">
						<div slot="content">
							<og-icon icon="right-from-bracket"></og-icon><span>${msg('Stop impersonating')}</span>
						</div>
					</og-button>
				` : null}

				${this.hasDB2 ? html`
					<div class="switch">
						<div style="flex: 1;"><og-icon icon="database"></og-icon><span>${msg('DB2')}</span></div>
						<og-switch
							class="field"
							shape="hexagon"
							.state="${rp.db > 1 ? 'on' : 'off'}"
							@change="${this._toggleDB2}">
						</og-switch>
					</div>
				` : null}

				<og-dropdown-menu
					noCaret
					dynamicWidth
					alignment="right"
					color="transparent"
					.items="${this.getThemeItems()}">
					<div slot="label">
						<og-icon icon="palette"></og-icon><span>${msg('Theme')}</span>
					</div>
				</og-dropdown-menu>

				<og-dropdown-menu
					noCaret
					dynamicWidth
					alignment="right"
					color="transparent"
					.items="${this.getLangItems()}">
					<div slot="label">
						<og-icon icon="globe"></og-icon><span>${langs[getLocale()]}</span>
					</div>
				</og-dropdown-menu>

				<og-button
					@click="${this._openUserAccount}"
					color="transparent">
					<div slot="content">
						<og-icon icon="circle-user"></og-icon><span>${msg('Account')}</span>
					</div>
				</og-button>

				<og-button
					@click="${this._openUserSettings}"
					color="transparent">
					<div slot="content">
						<og-icon icon="user-gear"></og-icon><span>${msg('Settings')}</span>
					</div>
				</og-button>

				${this.ssoLinks.map(link => html`
					<og-button
						@click="${() => this._openSsoLink(link)}"
						color="transparent">
						<div slot="content">
							<og-icon icon="arrow-up-right-from-square"></og-icon><span>${link.name}</span>
						</div>
					</og-button>
				`)}

				${this.compact ? html`
					<og-button
						@click="${this._openHelp}"
						color="transparent">
						<div slot="content">
							<og-icon icon="question-circle"></og-icon><span>${msg('Help')}</span>
						</div>
					</og-button>
				` : null}

				<og-button
					class="logout"
					@click="${() => this.container?.auth.logout()}"
					color="transparent">
					<div slot="content">
						<og-icon icon="power-off"></og-icon><span>${msg('Logout')}</span>
					</div>
				</og-button>
			</div>
		</og-dropdown>
	`;

}
